import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { DomEvent } from 'leaflet';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { ReactSVG } from 'react-svg';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import ArrowDropUp from '@material-ui/icons/ArrowDropUp';

import { useTranslation } from 'helpers/hooks';
import { ReactComponent as InfoIcon } from 'core/settings/Info/info.svg';

import { AppState } from '../../../store/interfaces';
import { toggleShowLayers, toggleLayer } from '../../../store/map/actions';
import {
  registerLayersInfoClickEvent,
  registerShowLayersClickEvent,
} from '../../../services/reactGa';
import { eventsService } from '../../../services/EventsService';
import { Checkbox } from '../../Checkbox';

const Container = styled.div`
  position: absolute;
  bottom: 10px;
  left: 10px;
  z-index: 1000;
  background: #fff;
  padding: 2px 8px;
  font-size: 16px;
  cursor: pointer;
  color: black;
  font-family: Roboto, serif;

  .layers-info-icon {
    width: 1.3em;
    height: 1.3em;
    margin-left: 8px;
  }

  .cluster-toggle,
  h2 {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;

    svg {
      width: 20px;
      height: 24px;
    }
  }

  .cluster-toggle {
    font-weight: bold;
  }
`;

const LayersContainer = styled.div`
  position: relative;

  h2 {
    font-size: 18px;
    text-align: center;
    margin: 4px 0 0;
  }

  font-size: 15px;

  .icon {
    position: relative;
    top: 2px;

    svg {
      max-height: 20px;
      max-width: 20px;
    }
  }

  table {
    border-collapse: separate;
    border-spacing: 4px 6px;

    tr td:last-child {
      padding-left: 12px;
    }

    b {
      margin-left: 4px;
    }
  }

  .cluster {
    position: relative;
    border-radius: 50%;
    width: 22px;
    height: 22px;
    margin-right: 3px;
    font-size: 13px;
    display: flex;
    justify-content: center;
    align-items: center;

    &__big {
      background: ${(props) => props.theme.map.clusters.big.color};
    }

    &__medium {
      background: ${(props) => props.theme.map.clusters.medium.color};
    }

    &__small {
      background: ${(props) => props.theme.map.clusters.small.color};
    }
  }

  .cluster__name {
    font-size: 15px;
    display: flex;
    align-items: center;
  }

  .checkbox {
    width: 20px;
    height: 20px;
    top: 2px;
  }
`;

export const Layers: React.FC = React.memo(function () {
  const ref = useRef();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { showLayers, layers } = useSelector(
    (app: AppState) => ({
      showLayers: app.map.showLayers,
      layers: app.map.layers,
    }),
    shallowEqual
  );

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    DomEvent.disableClickPropagation(ref.current);
  }, [ref]);

  const togglePanel = () => {
    if (!showLayers) {
      registerShowLayersClickEvent();
    }

    dispatch(toggleShowLayers());
  };

  const showModal = () => {
    const title = t('map.layers.clusters');
    const body = t('map.layers.clustersLegend');

    registerLayersInfoClickEvent();
    eventsService.showInfoModal({
      title,
      body,
    });
  };

  const toggle = (layer: string) => dispatch(toggleLayer(layer));

  return (
    <Container
      // @ts-ignore
      ref={ref}
      className="leaflet-touch leaflet-bar"
    >
      {showLayers ? (
        <LayersContainer>
          <h2 onClick={togglePanel}>
            {t('map.layers.title')} <ArrowDropDown fontSize="large" />
          </h2>
          <table>
            <tbody>
              <tr onClick={() => toggle('bigClusters')}>
                <td className="cluster__name">
                  <span className="cluster cluster__big">64</span> <b>{t('map.layers.clusters')}</b>{' '}
                  <InfoIcon onClick={showModal} className="layers-info-icon" />
                </td>
                <td>
                  <Checkbox value={layers.bigClusters} />
                </td>
              </tr>
              <tr onClick={() => toggle('inactivePins')}>
                <td>
                  <ReactSVG
                    wrapper="span"
                    className="icon"
                    src="/assets/map/pin_inactive_light.svg"
                  />{' '}
                  <b>{t('map.layers.inactivePins')}</b>
                </td>
                <td>
                  <Checkbox value={layers.inactivePins} />
                </td>
              </tr>
              <tr onClick={() => toggle('drivers')}>
                <td>
                  <ReactSVG
                    wrapper="span"
                    className="icon"
                    src="/assets/map/another_driver_black.svg"
                  />{' '}
                  <b>{t('map.layers.drivers')}</b>
                </td>
                <td>
                  <Checkbox value={layers.drivers} />
                </td>
              </tr>
            </tbody>
          </table>
        </LayersContainer>
      ) : (
        <div className="cluster-toggle" onClick={togglePanel}>
          <span>{t('map.layers.title')}</span> <ArrowDropUp fontSize="large" />
        </div>
      )}
    </Container>
  );
});
